import { useEffect, useMemo, useState } from 'react';
import { DownloadCloud, Loader2, UserPlus } from 'lucide-react';
import { practiceApi, errorDetailText } from '../../services/api';
import { CaseListItem, CnrLookupResponse } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { useClientsStore } from '../../store/clientsStore';
import { Button } from '../ui';

const COURT_TYPES: { value: string; label: string }[] = [
  { value: '', label: 'Court type…' },
  { value: 'district', label: 'District Court' },
  { value: 'high_court', label: 'High Court' },
  { value: 'supreme_court', label: 'Supreme Court' },
  { value: 'tribunal', label: 'Tribunal' },
  { value: 'consumer', label: 'Consumer Forum' },
  { value: 'other', label: 'Other' },
];

interface CaseFormProps {
  /** PATCH when set; POST (create) otherwise. */
  caseId?: string;
  initial?: Partial<CaseListItem>;
  /** Upgrade an existing plain matter into a case (create only). */
  matterId?: string;
  onSaved: (c: CaseListItem) => void;
  onCancel?: () => void;
}

type FieldKey =
  | 'title'
  | 'case_number'
  | 'cnr'
  | 'court_name'
  | 'court_type'
  | 'case_stage'
  | 'party_role'
  | 'opposite_party'
  | 'description';

const INPUT_CLS =
  'w-full text-sm border border-legal-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-accent';

/**
 * Conventional-UI case form (create + edit) — the non-chat parity path.
 * With the cnr_lookup feature, "Fetch from eCourts" prefills fields; anything
 * auto-filled carries an amber "auto — verify" hint until the advocate checks it.
 */
export function CaseForm({ caseId, initial, matterId, onSaved, onCancel }: CaseFormProps) {
  const user = useAuthStore((s) => s.user);
  const { clients, fetchClients, createClient } = useClientsStore();

  const [fields, setFields] = useState<Record<FieldKey, string>>({
    title: initial?.title ?? '',
    case_number: initial?.case_number ?? '',
    cnr: initial?.cnr ?? '',
    court_name: initial?.court_name ?? '',
    court_type: initial?.court_type ?? '',
    case_stage: initial?.case_stage ?? '',
    party_role: initial?.party_role ?? '',
    opposite_party: initial?.opposite_party ?? '',
    description: '',
  });
  const [clientId, setClientId] = useState(initial?.client_id ?? '');
  const [clientName, setClientName] = useState(initial?.client_name ?? '');
  // Inline "add new client" mini-form — creates a real Client record so the
  // call list gets tel:/wa.me buttons instead of a ghost free-text name.
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [clientError, setClientError] = useState('');
  // Extra keys from the CNR prefill that have no visible input (state_code,
  // district, case_type, case_year, next_hearing_date) — sent as-is on create.
  const [extras, setExtras] = useState<Record<string, string>>({});
  const [autoFilled, setAutoFilled] = useState<Set<string>>(new Set());
  const [warnings, setWarnings] = useState<string[]>([]);
  const [isFetchingCnr, setIsFetchingCnr] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const hasCnrLookup = !!user?.features?.cnr_lookup;

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const setField = (key: FieldKey, value: string) => {
    setFields((f) => ({ ...f, [key]: value }));
    // Editing an auto-filled value counts as verifying it.
    setAutoFilled((s) => {
      if (!s.has(key)) return s;
      const next = new Set(s);
      next.delete(key);
      return next;
    });
  };

  const fetchFromEcourts = async () => {
    const cnr = fields.cnr.trim();
    if (!cnr || isFetchingCnr) return;
    setIsFetchingCnr(true);
    setError('');
    setWarnings([]);
    try {
      const { data } = await practiceApi.cnrLookup(cnr);
      const res = data as CnrLookupResponse;
      const filled = new Set<string>();
      const nextFields = { ...fields };
      const nextExtras: Record<string, string> = { ...extras };
      Object.entries(res.prefill).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') return;
        const str = String(value);
        if (key in nextFields) {
          nextFields[key as FieldKey] = str;
          filled.add(key);
        } else {
          nextExtras[key] = str;
        }
      });
      setFields(nextFields);
      setExtras(nextExtras);
      setAutoFilled(filled);
      setWarnings(res.warnings ?? []);
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setError(errorDetailText(err, 'Daily eCourts lookup limit reached — try again tomorrow.'));
      } else {
        setError(errorDetailText(err, 'Could not fetch from eCourts — fill the details manually.'));
      }
    } finally {
      setIsFetchingCnr(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      const payload: Record<string, any> = {};
      (Object.keys(fields) as FieldKey[]).forEach((key) => {
        const v = fields[key].trim();
        if (v || caseId) payload[key] = v || undefined;
      });
      if (clientId) {
        payload.client_id = clientId;
        payload.client_name = undefined;
      } else if (clientName.trim()) {
        payload.client_name = clientName.trim();
      }
      Object.entries(extras).forEach(([k, v]) => {
        if (v) payload[k] = v;
      });
      if (!caseId && matterId) payload.matter_id = matterId;
      const { data } = caseId
        ? await practiceApi.updateCase(caseId, payload)
        : await practiceApi.createCase(payload);
      onSaved(data);
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not save the case. Please try again.'));
      setIsSaving(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim() || isCreatingClient) return;
    setIsCreatingClient(true);
    setClientError('');
    try {
      const client = await createClient({
        name: newClientName.trim(),
        ...(newClientPhone.trim() ? { phone: newClientPhone.trim() } : {}),
      });
      setClientId(client.id);
      setClientName('');
      setShowNewClient(false);
      setNewClientName('');
      setNewClientPhone('');
    } catch {
      setClientError('Could not save the client.');
    } finally {
      setIsCreatingClient(false);
    }
  };

  // One-tap "Save to Clients" for a free-text name — kills ghost clients.
  const handleSaveTypedClient = async () => {
    if (!clientName.trim() || isCreatingClient) return;
    setIsCreatingClient(true);
    setClientError('');
    try {
      const client = await createClient({ name: clientName.trim() });
      setClientId(client.id);
      setClientName('');
    } catch {
      setClientError('Could not save the client.');
    } finally {
      setIsCreatingClient(false);
    }
  };

  const autoHint = (key: string) =>
    autoFilled.has(key) ? (
      <span className="ml-1.5 text-[11px] font-medium text-amber-600">auto — verify</span>
    ) : null;

  const clientOptions = useMemo(
    () => clients.map((c) => ({ id: c.id, name: c.name })),
    [clients]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* CNR lookup — only when the server grants the feature */}
      {hasCnrLookup && !caseId && (
        <div className="p-3 bg-legal-50 border border-legal-200 rounded-lg space-y-2">
          <label className="block text-xs font-medium text-legal-600">
            Have the CNR? Fetch the case from eCourts.
          </label>
          <div className="flex gap-2">
            <input
              value={fields.cnr}
              onChange={(e) => setField('cnr', e.target.value)}
              placeholder="CNR — e.g. KLER010012342026"
              maxLength={30}
              className={`${INPUT_CLS} flex-1`}
            />
            <Button
              type="button"
              variant="secondary"
              size="md"
              data-track="cnr-fetch"
              onClick={fetchFromEcourts}
              disabled={!fields.cnr.trim() || isFetchingCnr}
              className="shrink-0"
            >
              {isFetchingCnr ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <DownloadCloud className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Fetch from eCourts</span>
              <span className="sm:hidden">Fetch</span>
            </Button>
          </div>
          {warnings.length > 0 && (
            <ul className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 space-y-1">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-legal-600 mb-1">
          Case title{autoHint('title')}
        </label>
        <input
          value={fields.title}
          onChange={(e) => setField('title', e.target.value)}
          maxLength={300}
          placeholder='e.g. "State v. Ravi — S.138 NI Act"'
          className={INPUT_CLS}
          autoFocus={!caseId}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-legal-600 mb-1">Client</label>
          <select
            value={showNewClient ? '__new__' : clientId}
            onChange={(e) => {
              if (e.target.value === '__new__') {
                setShowNewClient(true);
                setClientId('');
              } else {
                setShowNewClient(false);
                setClientId(e.target.value);
              }
            }}
            data-track="case-form-client-select"
            className={INPUT_CLS}
          >
            <option value="">No saved client — type a name below</option>
            {clientOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
            <option value="__new__">+ Add new client…</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-legal-600 mb-1">
            Client name {clientId ? '(using saved client)' : ''}
          </label>
          <input
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            maxLength={255}
            placeholder="Client name (free text)"
            disabled={!!clientId || showNewClient}
            className={`${INPUT_CLS} disabled:bg-legal-50 disabled:text-legal-400`}
          />
          {/* One-tap upgrade of a typed name into a real Client record */}
          {!clientId && !showNewClient && clientName.trim() && (
            <button
              type="button"
              data-track="case-form-save-to-clients"
              onClick={handleSaveTypedClient}
              disabled={isCreatingClient}
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-accent-700 bg-accent-50 border border-accent/40 rounded-full px-2.5 py-1 hover:bg-accent-100 transition-colors disabled:opacity-60"
            >
              {isCreatingClient ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <UserPlus className="h-3 w-3" />
              )}
              Save "{clientName.trim()}" to Clients
            </button>
          )}
        </div>
      </div>

      {/* Inline add-new-client mini-form — creates a real Client record */}
      {showNewClient && (
        <div className="p-3 bg-legal-50 border border-legal-200 rounded-lg space-y-2 animate-slide-up">
          <p className="text-xs font-semibold text-legal-700">New client</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              autoFocus
              value={newClientName}
              onChange={(e) => setNewClientName(e.target.value)}
              maxLength={255}
              placeholder="Client name"
              className={INPUT_CLS}
            />
            <input
              type="tel"
              value={newClientPhone}
              onChange={(e) => setNewClientPhone(e.target.value)}
              maxLength={20}
              placeholder="Phone (optional)"
              className={INPUT_CLS}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              data-track="case-form-add-client"
              onClick={handleCreateClient}
              isLoading={isCreatingClient}
              disabled={!newClientName.trim()}
            >
              <UserPlus className="h-3.5 w-3.5" /> Save client
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowNewClient(false);
                setClientError('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {clientError && <p className="text-xs text-red-600">{clientError}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-legal-600 mb-1">
            Case number{autoHint('case_number')}
          </label>
          <input
            value={fields.case_number}
            onChange={(e) => setField('case_number', e.target.value)}
            maxLength={100}
            placeholder="e.g. CC 123/2026"
            className={INPUT_CLS}
          />
        </div>
        {/* Plain CNR field when the lookup feature is off (or when editing) */}
        {(!hasCnrLookup || caseId) && (
          <div>
            <label className="block text-xs font-medium text-legal-600 mb-1">
              CNR{autoHint('cnr')}
            </label>
            <input
              value={fields.cnr}
              onChange={(e) => setField('cnr', e.target.value)}
              maxLength={30}
              placeholder="16-character eCourts number"
              className={INPUT_CLS}
            />
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-legal-600 mb-1">
            Court{autoHint('court_name')}
          </label>
          <input
            value={fields.court_name}
            onChange={(e) => setField('court_name', e.target.value)}
            maxLength={255}
            placeholder="e.g. JFCM-II, Ernakulam"
            className={INPUT_CLS}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-legal-600 mb-1">Court type</label>
          <select
            value={fields.court_type}
            onChange={(e) => setField('court_type', e.target.value)}
            className={INPUT_CLS}
          >
            {COURT_TYPES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-legal-600 mb-1">
            Stage{autoHint('case_stage')}
          </label>
          <input
            value={fields.case_stage}
            onChange={(e) => setField('case_stage', e.target.value)}
            maxLength={100}
            placeholder="e.g. Evidence"
            className={INPUT_CLS}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-legal-600 mb-1">Appearing for</label>
          <input
            value={fields.party_role}
            onChange={(e) => setField('party_role', e.target.value)}
            maxLength={100}
            placeholder="e.g. Complainant / Accused"
            className={INPUT_CLS}
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-legal-600 mb-1">
          Opposite party{autoHint('opposite_party')}
        </label>
        <input
          value={fields.opposite_party}
          onChange={(e) => setField('opposite_party', e.target.value)}
          maxLength={255}
          placeholder="Name of the other side"
          className={INPUT_CLS}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-legal-600 mb-1">Notes</label>
        <textarea
          value={fields.description}
          onChange={(e) => setField('description', e.target.value)}
          maxLength={5000}
          rows={2}
          placeholder="Anything worth remembering (optional)"
          className={`${INPUT_CLS} resize-none`}
        />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center gap-2 pt-1">
        <Button type="submit" size="md" isLoading={isSaving} disabled={!fields.title.trim() && !caseId}>
          {caseId ? 'Save case' : 'Create case'}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" size="md" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
